import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { BookOpen } from "lucide-react";

const TRACKS = ["all", "operator", "engineer", "hunter", "leader"];
const STAGES = ["all", "foundation", "specialization", "simulation", "certification", "recertification"];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [track, setTrack] = useState("all");
  const [stage, setStage] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (track !== "all") params.track = track;
    if (stage !== "all") params.stage = stage;
    const [c, e] = await Promise.all([
      api.get("/courses", { params }),
      api.get("/me/enrollments"),
    ]);
    setCourses(c.data); setEnrollments(e.data);
  }, [track, stage]);

  useEffect(() => { load(); }, [load]);

  const progressMap = Object.fromEntries(enrollments.map((e) => [e.course_id, e]));

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={20} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// COURSE_CATALOG</div>
        </div>
        <h1 className="font-display font-bold text-4xl uppercase mb-6" data-testid="courses-title">Catalog</h1>

        <div className="flex flex-wrap gap-3 mb-6">
          <Filter label="Track" value={track} setValue={setTrack} options={TRACKS} testid="filter-track" />
          <Filter label="Stage" value={stage} setValue={setStage} options={STAGES} testid="filter-stage" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => {
            const e = progressMap[c.id];
            return (
              <div key={c.id} className="flat-card p-5 flex flex-col" data-testid={`course-${c.code}`}>
                <Link to={`/courses/${c.id}`} className="flex-1" data-testid={`course-open-${c.code}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono text-xs text-[var(--cyan)]">{c.code}</div>
                      <div className="font-display font-bold text-lg uppercase mt-1 hover:text-[var(--cyan)] transition-colors">{c.title}</div>
                    </div>
                    <span className="tag">{c.stage}</span>
                  </div>
                  <p className="text-sm text-[var(--text-2)] mt-3 min-h-[48px]">{c.description}</p>
                  <div className="flex items-center justify-between font-mono text-xs text-[var(--text-2)] mt-3 uppercase tracking-widest">
                    <span>Track: {c.track}</span>
                    <span>{c.duration_hours}h</span>
                  </div>
                </Link>

                <div className="mt-4">
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${e?.progress || 0}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-xs text-[var(--text-2)]">{e ? `${e.progress}% · ${e.status}` : "Not enrolled"}</span>
                    <Link to={`/courses/${c.id}`} className="font-mono text-xs uppercase tracking-widest text-[var(--cyan)] hover:underline" data-testid={`course-detail-${c.code}`}>
                      Open →
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function Filter({ label, value, setValue, options, testid }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="flex gap-1 flex-wrap">
        {options.map((o) => (
          <button
            key={o}
            onClick={() => setValue(o)}
            data-testid={`${testid}-${o}`}
            className={`font-mono text-xs px-3 py-1 border uppercase tracking-widest ${
              value === o
                ? "bg-[var(--cyan)] text-[var(--bg)] border-[var(--cyan)]"
                : "border-[var(--border)] text-[var(--text-2)] hover:border-[var(--cyan)] hover:text-[var(--cyan)]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
